/**
 * Range resolution for the Token Usage tab. Pure, so it unit-tests under the `node`
 * vitest environment.
 *
 * The four U6 endpoints take a HALF-OPEN UTC window — `fromDate` inclusive, `toDate`
 * exclusive, both `YYYY-MM-DD` — and reject a span over 90 days with a 400. This module
 * is the only place that builds those parameters, so the cap is enforced once. That is
 * also why there is no "All time" preset here even though the signups DateRangeBar has
 * one: the server cannot serve it, and offering a chip that guarantees a 400 is worse
 * than not offering it.
 */

export type TokenUsageRangePreset = '7d' | '30d' | '90d' | 'custom';

export interface TokenUsageRange {
  preset: TokenUsageRangePreset;
  /** Custom window start, YYYY-MM-DD (inclusive). */
  customStart?: string;
  /** Custom window end, YYYY-MM-DD — INCLUSIVE in the UI, converted to exclusive here. */
  customEnd?: string;
}

export interface ResolvedTokenUsageRange {
  fromDate: string;
  /** Exclusive, per the wire contract. */
  toDate: string;
}

/** Server-side cap: `(toDate - fromDate).TotalDays > 90` is a 400. */
export const MAX_RANGE_DAYS = 90;

const MS_PER_DAY = 86_400_000;

const PRESET_DAYS: Record<Exclude<TokenUsageRangePreset, 'custom'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

export const TOKEN_USAGE_RANGE_LABELS: Record<TokenUsageRangePreset, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  custom: 'Custom',
};

/** UTC calendar day of an instant, as YYYY-MM-DD. */
export function utcDateKey(at: Date): string {
  return at.toISOString().slice(0, 10);
}

function addUtcDays(dateKey: string, days: number): string {
  return utcDateKey(new Date(Date.parse(`${dateKey}T00:00:00Z`) + days * MS_PER_DAY));
}

/** Whole days between two YYYY-MM-DD keys. NaN when either fails to parse. */
export function spanInDays(fromDate: string, toDate: string): number {
  const from = Date.parse(`${fromDate}T00:00:00Z`);
  const to = Date.parse(`${toDate}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return Number.NaN;
  return (to - from) / MS_PER_DAY;
}

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

function isValidDateKey(value: string | undefined): value is string {
  return !!value && DATE_KEY.test(value) && Number.isFinite(Date.parse(`${value}T00:00:00Z`));
}

export interface RangeResolution {
  /** Null whenever `error` is set — an invalid range sends NO request. */
  resolved: ResolvedTokenUsageRange | null;
  error: string | null;
}

/**
 * Resolve a UI range to the half-open window the endpoints take.
 *
 * Presets end at tomorrow's UTC date key so today's spend is included: "last 7 days"
 * is [today-6, today+1), a span of exactly 7. "Last 90 days" lands on exactly 90, which
 * is the largest span the server accepts (it rejects `> 90`, not `>= 90`).
 */
export function resolveTokenUsageRange(range: TokenUsageRange, now: Date): RangeResolution {
  const today = utcDateKey(now);

  if (range.preset !== 'custom') {
    const days = PRESET_DAYS[range.preset];
    return {
      resolved: { fromDate: addUtcDays(today, -(days - 1)), toDate: addUtcDays(today, 1) },
      error: null,
    };
  }

  if (!isValidDateKey(range.customStart) || !isValidDateKey(range.customEnd)) {
    return { resolved: null, error: 'Choose a start and end date.' };
  }
  if (range.customStart > range.customEnd) {
    return { resolved: null, error: 'Start date must be on or before the end date.' };
  }

  // The picker's end date is inclusive; the wire wants it exclusive.
  const toDate = addUtcDays(range.customEnd, 1);
  const span = spanInDays(range.customStart, toDate);
  if (span > MAX_RANGE_DAYS) {
    return {
      resolved: null,
      error: `Range cannot exceed ${MAX_RANGE_DAYS} days (selected ${span}).`,
    };
  }

  return { resolved: { fromDate: range.customStart, toDate }, error: null };
}

/**
 * The "data begins" boundary note, or null when the window is fully covered.
 *
 * Fires only when the org HAS records and the requested window reaches back before the
 * oldest one — so an empty stretch at the start of the chart is never read as a quiet
 * period when it is really the edge of retention. An org with no records at all returns
 * null here; that is the empty state's job to explain, not this note's.
 */
export function describeDataBegins(
  fromUtc: string,
  oldestRecordUtc: string | null
): string | null {
  if (!oldestRecordUtc) return null;
  const from = Date.parse(fromUtc);
  const oldest = Date.parse(oldestRecordUtc);
  if (!Number.isFinite(from) || !Number.isFinite(oldest)) return null;
  if (from >= oldest) return null;
  return `Data begins ${new Date(oldest).toISOString().slice(0, 10)} (UTC) — this range starts earlier than any retained record.`;
}
